// OWNED BY the notes implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/notes/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// הערות tab (mock s26): composer (Enter saves, 📌 pin, 🎤 voice note), pinned
// notes first with the warm highlighted style, then the rest newest-first —
// own notes with pin/share/edit/delete actions, teammates' org-shared notes
// read-only. The deprecated contact.additionalNotes free-text (when present)
// is surfaced once as a read-only legacy row so old data is never lost.
// Data: user-notes via useContactNotes (realtime, mirrors the SHARED
// sessionCache key `notes:{contactId}` that the overview panel + badge read).
import { useEffect, useState } from "react";
import { Box, Skeleton, Stack, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { useModal } from "mui-modal-provider";
import { useSnackbar } from "notistack5";
import Label from "components/Label";
import EmptyContent from "components/EmptyContent";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { ConfirmationDialog } from "components/__haim/Common/ConfirmationDialog";
import { TabCard } from "../TabCard";
import { getCached, subscribeCached } from "../data/sessionCache";
import { legacyAdditionalNote, notesCacheKey, preloadNotes, sortNotesNewestFirst } from "./notes/notesData";
import { useContactNotes } from "./notes/useContactNotes";
import { usePropertyNotes } from "./notes/usePropertyNotes";
import { propertyNotesCacheKey } from "./notes/propertyNotes";
import { NoteComposer } from "./notes/NoteComposer";
import { ContactNoteRow } from "./notes/ContactNoteRow";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { DisplayUserNote } from "hooks/useListingNotes";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

export default function NotesTab({ contact }: ContactTabComponentProps): JSX.Element {
  const theme = useTheme();
  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { userSettings } = useFirestore();

  const {
    notes,
    isLoading,
    canShareWithOrg,
    addNote,
    updateNote,
    deleteNote,
    setNotePinned,
    setNoteOrgShare
  } = useContactNotes(contact.id);

  // The notes she wrote on this contact's PROPERTY (the board's quick notes), beside the
  // contact's own — the seller and his property read as one record. Read-only here; they
  // are edited on the property. See `usePropertyNotes`.
  const propertyNotes = usePropertyNotes(contact);

  const myName: string = userSettings?.ownerFullName || user?.displayName || "אני";
  const legacyNote = legacyAdditionalNote(contact);

  // Display order: pinned first (newest-first within each group) — the shared
  // cache itself stays strictly newest-first (cross-tab contract). A property's
  // notes cannot be pinned here, so they join the rest by date.
  const pinnedNotes = notes.filter((note) => note.pinned === true);
  const regularNotes = sortNotesNewestFirst([...notes.filter((note) => note.pinned !== true), ...propertyNotes]);

  const totalCount = notes.length + propertyNotes.length + (legacyNote ? 1 : 0);
  const isEmpty = totalCount === 0;

  const mutationFailedSnackbar = (): void => {
    enqueueSnackbar("שמירת השינוי נכשלה", { variant: "error", autoHideDuration: 4000 });
  };

  const handleTogglePin = (note: DisplayUserNote): void => {
    void setNotePinned(note.id, note.pinned !== true).catch(mutationFailedSnackbar);
  };

  const handleToggleShare = (note: DisplayUserNote): void => {
    const isShared = (note.organizationIds?.length ?? 0) > 0;
    void setNoteOrgShare(note.id, !isShared).catch(mutationFailedSnackbar);
  };

  const handleSaveEdit = (note: DisplayUserNote, text: string): Promise<void> =>
    updateNote(note.id, text).catch((error) => {
      mutationFailedSnackbar();
      throw error;
    });

  const handleDelete = (note: DisplayUserNote): void => {
    const modal = showModal(ConfirmationDialog, {
      title: "מחיקת הערה",
      description: note.audioUrl
        ? "למחוק את ההערה הקולית לצמיתות?"
        : "למחוק את ההערה לצמיתות?",
      showCancel: true,
      showConfirm: true,
      onConfirm: () => {
        void deleteNote(note.id).catch(() => {
          enqueueSnackbar("מחיקת ההערה נכשלה", { variant: "error", autoHideDuration: 4000 });
        });
      },
      onClose: () => modal.hide()
    });
  };

  const renderRow = (note: DisplayUserNote): JSX.Element => (
    <ContactNoteRow
      key={`${note.listing?.listingId ?? ""}:${note.authorEmail}:${note.id}`}
      note={note}
      myName={myName}
      canShare={canShareWithOrg}
      onTogglePin={handleTogglePin}
      onToggleShare={handleToggleShare}
      onSaveEdit={handleSaveEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <Stack gap={2.5} sx={{ maxWidth: 880, mx: "auto", width: "100%" }}>
      <TabCard>
        <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 2 }}>
          <Typography variant="h6">הערות</Typography>
          {!isLoading && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {totalCount === 1 ? "הערה אחת" : `${totalCount} הערות`} · פנימיות בלבד
            </Typography>
          )}
        </Stack>

        <NoteComposer contactId={contact.id} addNote={addNote} />

        {isLoading && isEmpty ? (
          <NotesSkeleton />
        ) : isEmpty ? (
          <Box sx={{ mt: 1 }}>
            <EmptyContent
              title="אין הערות ללקוח"
              description="הערה ראשונה נשמרת ב-Enter — אפשר גם להקליט הערה קולית או לנעוץ חשובה למעלה."
            />
          </Box>
        ) : (
          <Box>
            {pinnedNotes.map(renderRow)}
            {regularNotes.map(renderRow)}

            {legacyNote && (
              <Stack
                direction="row"
                alignItems="flex-start"
                gap={1.5}
                sx={{ py: 2, borderTop: `1px dashed ${theme.palette.divider}` }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    flex: "none",
                    bgcolor: alpha(theme.palette.grey[500], 0.16),
                    color: "text.secondary"
                  }}
                  aria-hidden
                >
                  ⏳
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    dir="auto"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}
                  >
                    {legacyNote}
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.5 }}>
                    <Label color="default">גרסה קודמת</Label>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      הערה מכרטיס הלקוח הישן · לקריאה בלבד
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>
        )}
      </TabCard>
    </Stack>
  );
}

function NotesSkeleton(): JSX.Element {
  return (
    <Stack gap={2} sx={{ mt: 0.5 }}>
      {[0, 1, 2].map((index) => (
        <Stack key={index} direction="row" alignItems="flex-start" gap={1.5}>
          <Skeleton variant="circular" width={36} height={36} />
          <Stack sx={{ flexGrow: 1 }}>
            <Skeleton width="70%" height={20} />
            <Skeleton width="30%" height={16} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

// Badge for the tab strip: total notes count (incl. the read-only legacy
// additionalNotes row, so legacy-only contacts don't look empty, and the notes
// written on the contact's property once a mounted tab has read them). Reads ONLY
// from the shared sessionCache — the mounted tab / overview / preload keep the
// keys fresh; never fetches per render (tab-strip contract).
export function useNotesTabBadge(contact: RealEstateContact): TabBadge {
  const contactId = contact?.id ?? "";
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!contactId) return undefined;
    const rerender = (): void => forceRender((n) => n + 1);
    const unsubscribeNotes = subscribeCached(notesCacheKey(contactId), rerender);
    const unsubscribeProperty = subscribeCached(propertyNotesCacheKey(contactId), rerender);
    return () => {
      unsubscribeNotes();
      unsubscribeProperty();
    };
  }, [contactId]);

  const cached = contactId ? getCached<DisplayUserNote[]>(notesCacheKey(contactId)) : undefined;
  const property = contactId ? getCached<DisplayUserNote[]>(propertyNotesCacheKey(contactId)) : undefined;
  const count = (cached?.length ?? 0) + (property?.length ?? 0) + (legacyAdditionalNote(contact) ? 1 : 0);

  return count > 0 ? { count } : null;
}

// Cache warm-up fired on contacts-table row hover and page mount: one-shot
// fetch of the user's own notes into the shared `notes:{id}` key.
// Idempotent-cheap (fetchCached dedup) and never throws.
export function preloadNotesTab(contact: RealEstateContact): void {
  try {
    preloadNotes(contact);
  } catch {
    // preloads are best-effort by contract
  }
}
